import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import { authAPI } from '../../services/api';
import { Alert } from '../common/Alert';
import { Input } from '../common/Input';
import { Button } from '../common/Button';

export const ForgotPassword: React.FC = () => {
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState<'user' | 'supplier' | ''>('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.forgotPassword(email, userType || undefined);
      if (response.success) {
        setStep('reset');
      } else {
        let errorMessage = response.message || 'Failed to send OTP. Please try again.';
        
        // Add helpful suggestions for common issues
        if (errorMessage.includes('No account found')) {
          if (userType) {
            errorMessage += ` Try selecting "Any account type" or a different account type.`;
          } else {
            errorMessage += ` You may need to create an account first.`;
          }
        }
        
        setError(errorMessage);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authAPI.resetPassword(email, otpCode, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#63B56F] to-[#193B4E] py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-2xl"
      >
        {!success ? (
          <>
            <div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                {step === 'email' ? 'Forgot Password' : 'Reset Password'}
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                {step === 'email' 
                  ? 'Enter your email to receive a password reset code'
                  : 'Enter the OTP code sent to your email'}
              </p>
            </div>

            {error && (
              <div className="space-y-3">
                <Alert type="error" message={error} onClose={() => setError('')} />
                {error.includes('No account found') && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Need an account?</h4>
                    <div className="flex flex-col sm:flex-row gap-2 text-sm">
                      <Link
                        to="/user/signup"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Create User Account
                      </Link>
                      <span className="hidden sm:inline text-blue-400">•</span>
                      <button
                        type="button"
                        onClick={() => setUserType('')}
                        className="text-blue-600 hover:text-blue-800 underline text-left"
                      >
                        Try "Any account type"
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 'email' ? (
              <form className="mt-8 space-y-6" onSubmit={handleSendOTP}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Type (optional)
                    </label>
                    <select
                      value={userType}
                      onChange={(e) => setUserType(e.target.value as 'user' | 'supplier' | '')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#63B56F] focus:border-[#63B56F]"
                    >
                      <option value="">Any account type</option>
                      <option value="user">User Account</option>
                      <option value="supplier">Supplier Account</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select if you have the same email for multiple account types, or leave as "Any account type" to search all
                    </p>
                  </div>
                  
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    loading={loading}
                  >
                    Send Reset Code
                  </Button>

                  <Link
                    to="/login"
                    className="flex items-center justify-center text-sm text-[#63B56F] hover:text-[#4A9B57]"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Login
                  </Link>
                </div>
              </form>
            ) : (
              <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
                <div className="space-y-4">
                  <div>
                    <Input
                      type="text"
                      placeholder="Enter OTP code"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      maxLength={6}
                      required
                    />
                  </div>

                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="New password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 pr-10"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    loading={loading}
                  >
                    Reset Password
                  </Button>

                  <button
                    type="button"
                    onClick={() => setStep('email')}
                    className="w-full text-sm text-[#63B56F] hover:text-[#4A9B57]"
                  >
                    Change email address
                  </button>
                </div>
              </form>
            )}
          </>
        ) : (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center"
          >
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Password Reset Successful!
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Your password has been reset successfully. Redirecting to login...
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};